/**
 * 🛥️ YACHT APPROVAL SERVICE
 * Handle yacht registration approvals and management
 */

import { getApiBaseUrl } from '@/config/environment';

const getAPIBase = () => getApiBaseUrl();

// ==================== TYPES ====================

export interface YachtInfo {
  name: string;
  manufacturer: string;
  model: string;
  yearBuilt: string;
  type: string; // 'motor_yacht' | 'sailing_yacht' | 'catamaran' | 'gulet' | 'mega_yacht'
  length: number; // in feet
  beam: number; // width in feet
  draft: number; // depth in feet
  homePort: string;
  registrationNumber: string;
  flag: string; // country of registration
  guestCapacity: number;
  crewCapacity: number;
  cabins: number;
  bathrooms?: number;
  state?: string;
  city?: string;
  features?: string[]; // e.g., 'wifi', 'ac', 'kitchen', 'water_sports'
}

export interface YachtPricingInfo {
  // Time-based charter rates
  hourlyRate?: number | null;
  halfDayRate?: number | null;
  fullDayRate?: number | null; // Same as charterRate
  charterRate: number; // daily charter rate (legacy)
  weeklyRate?: number | null;

  // Additional fees
  captainFee?: number | null;
  crewFee?: number | null;
  fuelSurcharge?: number | null;
  cleaningFee?: number | null;
  securityDeposit?: number | null;

  // Booking rules
  minimumBookingHours?: number | null;
  currency: string; // 'NGN' | 'USD' | 'EUR' | 'GBP'
}

export interface YachtInsuranceInfo {
  provider: string;
  policyNumber: string;
  expiryDate: string;
  coverage: number;
  verified: boolean;
}

export interface YachtHostInfo {
  id: string;
  name: string;
  email: string | null;
  phone: string;
  profilePicture: string | null;
  companyName?: string | null;
}

export interface YachtPhotoInfo {
  id: string;
  url: string;
  type: 'exterior' | 'interior' | 'cabin' | 'deck' | 'other';
  isPrimary: boolean;
  caption?: string;
}

export interface YachtReviewInfo {
  reviewedBy: string | null;
  reviewedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedBy: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  adminNotes: string | null;
}

export interface PendingYacht {
  id: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  yacht: YachtInfo;
  insurance: YachtInsuranceInfo;
  host: YachtHostInfo;
  pricing: YachtPricingInfo;
  photos: {
    count: number;
    items: YachtPhotoInfo[];
  };
  instantBooking: boolean;
  review: YachtReviewInfo;
  createdAt: string;
  updatedAt: string;
  isActive?: boolean;
  suspensionStatus?: 'active' | 'suspended' | 'blacklisted';
  amenities?: string[];
  rules?: string[];
  cancellationPolicy?: string;
}

export interface YachtApprovalsResponse {
  success: boolean;
  data: {
    yachts: PendingYacht[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
  message?: string;
}

export interface YachtActionResponse {
  success: boolean;
  data?: {
    yacht: PendingYacht;
    message: string;
  };
  message?: string;
  error?: string;
}

// ==================== API FUNCTIONS ====================

export const yachtApprovalService = {
  /**
   * Get pending yacht approvals with optional filters
   */
  async getPendingYachts(filters?: {
    status?: 'draft' | 'pending_approval' | 'approved' | 'rejected';
    limit?: number;
    offset?: number;
    yacht_type?: string;
    home_port?: string;
    min_length?: number;
    max_length?: number;
  }): Promise<YachtApprovalsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      if (filters?.yacht_type) queryParams.append('yacht_type', filters.yacht_type);
      if (filters?.home_port) queryParams.append('home_port', filters.home_port);
      if (filters?.min_length) queryParams.append('min_length', filters.min_length.toString());
      if (filters?.max_length) queryParams.append('max_length', filters.max_length.toString());

      const apiBase = getAPIBase();
      const url = `${apiBase}/api/v1/admin/yachts/pending${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Error fetching pending yachts:', error);
      return {
        success: false,
        data: {
          yachts: [],
          pagination: {
            total: 0,
            limit: 0,
            offset: 0,
            hasMore: false
          }
        },
        message: error instanceof Error ? error.message : 'Failed to fetch pending yachts'
      };
    }
  },

  /**
   * Approve a yacht registration
   */
  async approveYacht(
    yachtId: string,
    adminUserId: string,
    notes?: string
  ): Promise<YachtActionResponse> {
    try {
      const requestBody = {
        action: 'approve',
        adminUserId,
        notes: notes || undefined
      };


      const response = await fetch(`${getAPIBase()}/api/v1/admin/yachts/${yachtId}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('❌ Backend error response:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Error approving yacht:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to approve yacht'
      };
    }
  },

  /**
   * Reject a yacht registration
   */
  async rejectYacht(
    yachtId: string,
    adminUserId: string,
    reason: string
  ): Promise<YachtActionResponse> {
    try {

      if (!reason || reason.trim() === '') {
        throw new Error('Rejection reason is required');
      }

      const requestBody = {
        action: 'reject',
        adminUserId,
        notes: reason
      };


      const response = await fetch(`${getAPIBase()}/api/v1/admin/yachts/${yachtId}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('❌ Backend error response:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Error rejecting yacht:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reject yacht'
      };
    }
  },

  /**
   * Revert yacht to pending (unapprove/unreject)
   */
  async revertToPending(
    yachtId: string,
    adminUserId: string,
    notes?: string
  ): Promise<YachtActionResponse> {
    try {
      const url = `${getAPIBase()}/api/v1/admin/yachts/${yachtId}/review`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'revert_to_pending',
          adminUserId,
          notes: notes || 'Reverting to pending for re-review'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Revert to Pending API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error reverting yacht to pending:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to revert yacht to pending'
      };
    }
  },

  /**
   * Update yacht pricing
   */
  async updateYachtPricing(
    yachtId: string,
    pricing: {
      charterRate?: number;
      weeklyRate?: number;
      captainFee?: number;
      crewFee?: number;
      fuelSurcharge?: number;
      cleaningFee?: number;
      securityDeposit?: number;
      currency?: string;
      adminUserId: string;
    }
  ): Promise<Response> {
    try {

      const response = await fetch(`${getAPIBase()}/api/v1/admin/yachts/${yachtId}/pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(pricing)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
          console.error('❌ Backend error response:', errorData);
        } catch (e) {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('❌ Error updating yacht pricing:', error);
      throw error;
    }
  },

  /**
   * Update yacht details
   */
  async updateYachtDetails(
    yachtId: string,
    details: {
      registrationNumber?: string;
      state?: string;
      city?: string;
      homePort?: string;
      yearBuilt?: string;
      guestCapacity?: number;
      crewCapacity?: number;
      cabins?: number;
      bathrooms?: number;
      adminUserId: string;
    }
  ): Promise<Response> {
    try {

      const response = await fetch(`${getAPIBase()}/api/v1/admin/yachts/${yachtId}/details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(details)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
          console.error('❌ Backend error response:', errorData);
        } catch (e) {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('❌ Error updating yacht details:', error);
      throw error;
    }
  },

  /**
   * Toggle yacht visibility (active/inactive)
   */
  async toggleYachtVisibility(
    yachtId: string,
    isActive: boolean,
    adminUserId: string
  ): Promise<YachtActionResponse> {
    try {
      const apiBaseUrl = getAPIBase();
      const url = `${apiBaseUrl}/api/v1/admin/yachts/${yachtId}/toggle-active`;


      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          adminUserId,
          isActive,
          reason: `Admin ${adminUserId} ${isActive ? 'activated' : 'deactivated'} yacht`
        })
      });


      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('❌ Yacht toggle failed:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(`HTTP error! status: ${response.status} - ${errorData?.error?.message || errorData?.message || 'Unknown error'}`);
      }

      const data = await response.json();

      // Backend returns yacht data in 'yacht' field, not 'data'
      const updatedYacht = data.yacht || data.data;
      const newIsActive = updatedYacht?.isActive ?? updatedYacht?.is_active;


      // Return normalized response with yacht data in 'data' field for consistency
      return {
        ...data,
        data: updatedYacht
      };
    } catch (error) {
      console.error('❌ Error toggling yacht visibility:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to toggle yacht visibility'
      };
    }
  },

  /**
   * Get single yacht details
   */
  async getYachtDetails(yachtId: string): Promise<YachtActionResponse> {
    try {
      const response = await fetch(`${getAPIBase()}/api/v1/admin/yachts/${yachtId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Error fetching yacht details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch yacht details'
      };
    }
  }
};
